
import { type ReactNode } from 'react';
import { cn } from '@/lib/utils';

export interface FilterChipProps {
  label: string;
  isSelected: boolean;
  onClick: () => void;
  icon?: ReactNode;
  actionIcon?: ReactNode;
  variant?: 'default' | 'outline' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  badgeCount?: number;
  showBadge?: boolean;
}

const FilterChip = ({
  label,
  isSelected,
  onClick,
  icon,
  actionIcon,
  variant = 'default',
  size = 'md',
  className,
  badgeCount,
  showBadge = false,
}: FilterChipProps) => {
  // Size classes
  const sizeClasses = {
    sm: 'px-2 py-1 text-xs',
    md: 'px-3 py-1.5 text-sm',
    lg: 'px-4 py-2 text-base'
  };
  
  // Variant classes with theme-matching primary color
  const variantClasses = {
    default: isSelected 
      ? 'bg-propertylk-blue text-white font-medium shadow-sm'
      : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
    outline: isSelected
      ? 'border-2 border-propertylk-blue bg-propertylk-blue/10 text-propertylk-blue font-medium'
      : 'border border-gray-200 bg-white text-gray-700 hover:border-gray-300',
    ghost: isSelected
      ? 'bg-transparent text-propertylk-blue font-medium underline'
      : 'bg-transparent text-gray-700 hover:text-propertylk-blue'
  };

  return (
    <button
      className={cn(
        'rounded-md transition-all flex items-center gap-1.5',
        sizeClasses[size],
        variantClasses[variant],
        className
      )}
      onClick={onClick}
    >
      {icon && <span className={isSelected ? (variant === 'default' ? 'text-white' : 'text-propertylk-blue') : 'text-gray-500'}>{icon}</span>}
      <span>{label}</span>
      {showBadge && badgeCount && badgeCount > 0 && (
        <span className={cn(
          "ml-1 inline-flex items-center justify-center rounded-full px-1.5 text-xs",
          isSelected && variant === 'default' 
            ? "bg-white/30 text-white" 
            : "bg-gray-200 text-gray-700"
        )}>
          {badgeCount}
        </span>
      )}
      {actionIcon && !isSelected && <span className="text-gray-500">{actionIcon}</span>}
    </button>
  );
};

export default FilterChip;
